import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BillingPanel from "../../DriverBilling/BillingPanel";

/**
 * SettingsPage — /settings
 *
 * Hosts the account tabs. Billing lives at /settings?tab=billing, which is the
 * address Stripe Checkout, the Stripe billing portal and the TopBar menu all
 * return to, so the `tab` param has to be honoured here.
 *
 * Route (add beside /user-dashboard, inside the same layout that renders
 * TopBar and the sidebar):
 *
 *   <Route path="/settings" element={<SettingsPage />} />
 *
 * It must sit ABOVE your <Route path="*" element={<NotFound />} /> catch-all.
 */

const API = process.env.REACT_APP_API_URL;
const NAVY = "#1a2744";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

async function api(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const detail =
      data.message ||
      data.error ||
      data.detail ||
      (typeof data === "object" && Object.values(data)[0]) ||
      "Something went wrong.";
    throw new Error(Array.isArray(detail) ? detail[0] : detail);
  }
  return data;
}

const TABS = [
  { id: "account", label: "Account" },
  { id: "billing", label: "Billing and membership" },
];

const ROLE_LABEL = {
  pilot: "Pilot driver",
  company: "Company",
  dispatcher: "Dispatcher",
};

/* ─── shared pieces ──────────────────────────────────────────────────────── */

function Card({ title, description, children, footer }) {
  return (
    <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-5 sm:px-6 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
      <div className="px-5 sm:px-6 py-5">{children}</div>
      {footer && (
        <div className="px-5 sm:px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
          {footer}
        </div>
      )}
    </section>
  );
}

function Notice({ tone = "info", children }) {
  const tones = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    error: "bg-red-50 border-red-200 text-red-700",
    success: "bg-green-50 border-green-200 text-green-800",
  };
  return (
    <div className={`border rounded-xl px-4 py-3 text-sm ${tones[tone]}`} role="status">
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

function Button({ variant = "primary", busy, children, className = "", ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500";
  const styles = {
    primary: "text-white hover:opacity-90",
    secondary: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50",
  };
  return (
    <button
      {...rest}
      disabled={busy || rest.disabled}
      style={variant === "primary" ? { background: NAVY } : undefined}
      className={`${base} ${styles[variant]} ${className}`}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-500 mt-1.5">{hint}</span>}
    </label>
  );
}

const inputClass =
  "w-full bg-white border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-gray-400 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-500";

/* ─── account tab ────────────────────────────────────────────────────────── */

function AccountTab({ onUserChange }) {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [resetState, setResetState] = useState({ busy: false, message: "", error: "" });

  useEffect(() => {
    let cancelled = false;
    api("/api/me/")
      .then((data) => {
        if (cancelled) return;
        setMe(data);
        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone: data.phone || "",
        });
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(() => {
    if (!me) return false;
    return (
      form.first_name !== (me.first_name || "") ||
      form.last_name !== (me.last_name || "") ||
      form.phone !== (me.phone || "")
    );
  }, [form, me]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await api("/api/me/", { method: "PATCH", body: form });
      setMe(updated);
      setSaved(true);
      onUserChange?.(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendReset = async () => {
    setResetState({ busy: true, message: "", error: "" });
    try {
      const res = await fetch(`${API}/api/password-reset/request/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: me.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not send the reset link.");
      setResetState({ busy: false, message: data.message, error: "" });
    } catch (e) {
      setResetState({ busy: false, message: "", error: e.message });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-28 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!me) {
    return <Notice tone="error">{error || "Your account details could not be loaded."}</Notice>;
  }

  return (
    <div className="space-y-5">
      <Card
        title="Your details"
        description="Shown to companies and dispatchers on jobs you take."
        footer={
          <>
            {saved && !dirty && <span className="text-sm text-green-700 sm:mr-auto">Changes saved.</span>}
            <Button onClick={save} busy={saving} disabled={!dirty}>
              Save changes
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {error && <Notice tone="error">{error}</Notice>}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name">
              <input className={inputClass} value={form.first_name} onChange={set("first_name")} />
            </Field>
            <Field label="Last name">
              <input className={inputClass} value={form.last_name} onChange={set("last_name")} />
            </Field>
          </div>

          <Field label="Phone">
            <input
              className={inputClass}
              value={form.phone}
              onChange={set("phone")}
              placeholder="(555) 555-0123"
              inputMode="tel"
            />
          </Field>

          <Field
            label="Email"
            hint="Your email is your sign-in address. Contact support to change it — a new address has to be verified before it can be used."
          >
            <input className={inputClass} value={me.email || ""} disabled />
          </Field>
        </div>
      </Card>

      <Card title="Account type">
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="text-gray-500 mb-0.5">Role</dt>
            <dd className="font-semibold text-gray-900">{ROLE_LABEL[me.role] || me.role || "—"}</dd>
          </div>
          <div>
            <dt className="text-gray-500 mb-0.5">Email verified</dt>
            <dd className="font-semibold text-gray-900">{me.is_verified ? "Yes" : "Not yet"}</dd>
          </div>
          {"is_badge_verified" in me && (
            <div>
              <dt className="text-gray-500 mb-0.5">Verified badge</dt>
              <dd className="font-semibold text-gray-900">
                {me.is_badge_verified ? "Issued" : "Not issued"}
              </dd>
            </div>
          )}
        </dl>
      </Card>

      <Card
        title="Password"
        description="We send a reset link to your email rather than changing the password in the browser."
        footer={
          <Button variant="secondary" onClick={sendReset} busy={resetState.busy}>
            Email me a reset link
          </Button>
        }
      >
        {resetState.error && <Notice tone="error">{resetState.error}</Notice>}
        {resetState.message && <Notice tone="success">{resetState.message}</Notice>}
        {!resetState.error && !resetState.message && (
          <p className="text-sm text-gray-600">
            The link goes to <strong className="text-gray-900">{me.email}</strong> and expires five
            minutes after it's sent.
          </p>
        )}
      </Card>
    </div>
  );
}

/* ─── page ───────────────────────────────────────────────────────────────── */

export default function DriverSettings({ onUserChange }) {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const active = TABS.some((t) => t.id === requested) ? requested : "account";

  const selectTab = useCallback(
    (id) => {
      // Replace the whole query string so a stale ?checkout= param from a
      // Stripe redirect doesn't linger once the user moves on.
      setParams(id === "account" ? {} : { tab: id }, { replace: true });
    },
    [setParams]
  );

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto w-full">
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="Settings sections">
          {TABS.map((tab) => {
            const selected = tab.id === active;
            return (
              <button
                key={tab.id}
                onClick={() => selectTab(tab.id)}
                aria-current={selected ? "page" : undefined}
                className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-t ${
                  selected
                    ? "text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                style={selected ? { borderColor: NAVY, color: NAVY } : undefined}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {active === "account" && <AccountTab onUserChange={onUserChange} />}
      {active === "billing" && <BillingPanel />}
    </div>
  );
}
